import {
	buildAttachmentsMacroDataQueryVariables,
	buildAttachmentsPageByTitleQueryVariables,
	resolveAttachmentsPageId,
} from '@confluence/attachments-macro/entry-points/attachmentsMacroQueryVariables.ts';
import { USE_ATTACHMENTS_MACRO_DATA_QUERY } from '@confluence/attachments-macro/entry-points/useAttachmentsMacroDataQuery.ts';
import type {
	useAttachmentsMacroDataQuery$data,
	useAttachmentsMacroDataQuery$variables,
} from '@confluence/attachments-macro/entry-points/useAttachmentsMacroDataQueryTypes.ts';
import {
	parsePageRef,
	USE_ATTACHMENTS_PAGE_BY_TITLE_QUERY,
} from '@confluence/attachments-macro/entry-points/useAttachmentsPageByTitleQuery.ts';
import type {
	useAttachmentsPageByTitleQuery$data,
	useAttachmentsPageByTitleQuery$variables,
} from '@confluence/attachments-macro/entry-points/useAttachmentsPageByTitleQueryTypes.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';

import type { MacroPreloadHandler } from '../preloadMacroData.ts';

export const attachmentsPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'attachments',
	routes: ['view', 'space-overview'],
	preload: async (node, context) => {
		try {
			await slowQueryGuard(
				'attachmentsPreload',
				async () => {
					const pageRef = node.attrs?.parameters?.macroParams?.page?.value;
					const target = pageRef ? parsePageRef(pageRef, context.spaceKey) : null;
					let contentId: string | null = context.contentId;
					if (target?.rawTitle && target.rawSpaceKey) {
						const result = await query<
							useAttachmentsPageByTitleQuery$data,
							useAttachmentsPageByTitleQuery$variables
						>({
							query: USE_ATTACHMENTS_PAGE_BY_TITLE_QUERY,
							variables: buildAttachmentsPageByTitleQueryVariables(target),
							context: { single: true },
						});
						contentId = resolveAttachmentsPageId(result.data, target);
					}
					if (!contentId) {
						return;
					}
					await query<useAttachmentsMacroDataQuery$data, useAttachmentsMacroDataQuery$variables>({
						query: USE_ATTACHMENTS_MACRO_DATA_QUERY,
						variables: buildAttachmentsMacroDataQueryVariables(contentId),
						context: { single: true },
					});
				},
				SLOW_QUERY_DEFAULT_TIMEOUT,
			);
		} catch {
			// A failed preload must leave the existing client fetch available.
			return;
		}
	},
};
